import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useCustomer } from '../context/CustomerContext';
import { useCurrency } from '../context/CurrencyContext';
import './CustomerDashboard.css';

const CustomerDashboard = () => {
  const { currency } = useCurrency();
  const [customerInfo, setCustomerInfo] = useState(null);
  const [activeSection, setActiveSection] = useState('orders');

  // Function to get currency symbol
  const getCurrencySymbol = () => {
    if (currency === '$') return '$';
    else if (currency === '£') return '£';
    else if (currency === 'AED') return 'د.إ';
    else if (currency === 'KSA') return 'ر.س';
    return '£'; // Default fallback
  };
  const [orders, setOrders] = useState([]);
  const [reviewOpportunities, setReviewOpportunities] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedReviewOpportunity, setSelectedReviewOpportunity] = useState(null);
  const [notification, setNotification] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    title: '',
    comment: ''
  });
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });
  const navigate = useNavigate();
  const { customer, logout } = useCustomer();

  useEffect(() => {
    // Check if customer is logged in
    const customerId = localStorage.getItem('customerId');
    const customerEmail = localStorage.getItem('customerEmail');
    const customerName = localStorage.getItem('customerName');

    if (!customerId || !customerEmail) {
      navigate('/login');
      return;
    }

    setCustomerInfo({
      id: customerId,
      email: customerEmail,
      name: customerName
    });

    // Load customer data
    loadCustomerData();
    loadReviewData();
  }, [navigate]);

  const loadCustomerData = async () => {
    setLoading(true);
    try {
      const customerId = localStorage.getItem('customerId');
      const customerEmail = localStorage.getItem('customerEmail');

      // Load customer orders
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select('*')
        .eq('customer_email', customerEmail)
        .order('created_at', { ascending: false });

      if (ordersError) {
        console.error('Error loading orders:', ordersError);
      } else {
        setOrders(ordersData || []);
      }

      // Load customer profile
      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .select('*')
        .eq('id', customerId)
        .single();

      if (customerError) {
        console.error('Error loading customer profile:', customerError);
      } else if (customerData) {
        setProfileForm({
          first_name: customerData.first_name || '',
          last_name: customerData.last_name || '',
          email: customerData.email || '',
          phone: customerData.phone || ''
        });
      }

    } catch (error) {
      console.error('Error loading customer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadReviewData = async () => {
    try {
      const customerEmail = localStorage.getItem('customerEmail');
      if (!customerEmail) {
        console.log('No customer email found');
        return;
      }

      console.log('Loading reviews for email:', customerEmail);

      // First, let's try a simple query to see what columns exist
      const { data: allOrders, error: allOrdersError } = await supabase
        .from('orders')
        .select('*')
        .limit(1);

      if (allOrdersError) {
        console.error('Error checking orders table structure:', allOrdersError);
        return;
      }

      console.log('Orders table structure:', allOrders);

      // Now try to get delivered orders with basic fields
      const { data: deliveredOrders, error: ordersError } = await supabase
        .from('orders')
        .select('*')
        .eq('status', 'delivered')
        .order('created_at', { ascending: false });

      if (ordersError) {
        console.error('Error loading delivered orders:', ordersError);
        return;
      }

      console.log('All delivered orders:', deliveredOrders);

      // Filter orders by customer email
      const customerOrders = deliveredOrders?.filter(order => {
        // Try different possible email field names
        const emailMatch = order.customer_email === customerEmail || 
                          order.email === customerEmail ||
                          order.customer?.email === customerEmail;
        console.log(`Checking order ${order.id || order.order_number}: email match = ${emailMatch}`);
        return emailMatch;
      }) || [];

      console.log('Customer delivered orders:', customerOrders);

      if (customerOrders.length === 0) {
        console.log('No delivered orders found for this email');
        setReviewOpportunities([]);
        setReviews([]);
        return;
      }

      // Create review opportunities from delivered orders
      const reviewOpportunities = [];
      const processedProducts = new Set();

      for (const order of customerOrders) {
        console.log('Processing order (full structure):', JSON.stringify(order, null, 2));
        
        // Try to get order items from different possible structures
        let orderItems = [];
        
        if (order.order_items && Array.isArray(order.order_items)) {
          orderItems = order.order_items;
          console.log('Found order_items array');
        } else if (order.items && Array.isArray(order.items)) {
          orderItems = order.items;
          console.log('Found items array');
        } else if (order.products && Array.isArray(order.products)) {
          orderItems = order.products;
          console.log('Found products array');
        } else {
          console.log('No order items found in any expected format');
          console.log('Available fields in order:', Object.keys(order));
          
          // Check for any fields that might contain product data
          for (const [key, value] of Object.entries(order)) {
            if (Array.isArray(value) && value.length > 0) {
              console.log(`Found array field "${key}":`, value);
            }
            if (typeof value === 'string' && (value.includes('product') || value.includes('item'))) {
              console.log(`Found string field "${key}" that might contain product data:`, value);
            }
          }
        }

        console.log('Order items found:', orderItems);

        for (const item of orderItems) {
          const productId = item.product_id || item.id;
          if (!productId) continue;

          const productKey = `${productId}_${order.id}`;
          if (!processedProducts.has(productKey)) {
            // Check if review already exists
            const { data: existingReview } = await supabase
              .from('reviews')
              .select('id')
              .eq('customer_email', customerEmail)
              .eq('product_id', productId)
              .eq('order_id', order.id)
              .maybeSingle();

            if (!existingReview) {
              // Get product details
              const { data: product, error: productError } = await supabase
                .from('products')
                .select('id, name, image_url')
                .eq('id', productId)
                .maybeSingle();

              if (product && !productError) {
                reviewOpportunities.push({
                  id: `${order.id}_${productId}`,
                  customer_email: customerEmail,
                  product_id: productId,
                  order_id: order.id,
                  order_number: order.order_number || order.id,
                  verified_purchase: true,
                  status: 'pending',
                  products: product,
                  created_at: order.created_at
                });
                console.log('Added review opportunity for product:', product.name);
              } else {
                console.log('Product not found or error:', productError);
              }
              processedProducts.add(productKey);
            } else {
              console.log('Review already exists for product:', productId);
            }
          }
        }
      }

      console.log('Total review opportunities found:', reviewOpportunities);
      setReviewOpportunities(reviewOpportunities);

      // Load existing reviews
      const { data: existingReviews, error: reviewsError } = await supabase
        .from('reviews')
        .select('*')
        .eq('customer_email', customerEmail);

      if (reviewsError) {
        console.error('Error loading reviews:', reviewsError);
        setReviews([]);
      } else {
        console.log('Existing reviews found:', existingReviews);
        
        // Get product details for each review
        const reviewsWithProducts = [];
        for (const review of existingReviews || []) {
          const { data: product } = await supabase
            .from('products')
            .select('id, name, image_url')
            .eq('id', review.product_id)
            .maybeSingle();
          
          reviewsWithProducts.push({
            ...review,
            products: product
          });
        }
        
        setReviews(reviewsWithProducts);
      }
    } catch (error) {
      console.error('Error in loadReviewData:', error);
    }
  };

  const handleWriteReview = (opportunity) => {
    setSelectedReviewOpportunity(opportunity);
    setReviewForm({
      rating: 5,
      title: '',
      comment: ''
    });
    setShowReviewModal(true);
  };

  const handleSubmitReview = async () => {
    try {
      setLoading(true);

      const customerEmail = localStorage.getItem('customerEmail');
      
      const { error: reviewError } = await supabase
        .from('reviews')
        .insert({
          customer_email: customerEmail,
          product_id: selectedReviewOpportunity.product_id,
          order_id: selectedReviewOpportunity.order_id,
          rating: reviewForm.rating,
          title: reviewForm.title,
          comment: reviewForm.comment,
          verified_purchase: true,
          status: 'pending'
        });

      if (reviewError) {
        console.error('Error submitting review:', reviewError);
        showNotification('Failed to submit review. Please try again.', 'error');
        return;
      }

      console.log('Review submitted successfully');

      // Reload review data
      await loadReviewData();
      
      showNotification('Review submitted successfully! It will be visible after approval.', 'success');
      setShowReviewModal(false);
      setSelectedReviewOpportunity(null);
    } catch (error) {
      console.error('Error submitting review:', error);
      showNotification('An error occurred while submitting your review.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('customerId');
    localStorage.removeItem('customerEmail');
    localStorage.removeItem('customerName');
    logout();
    navigate('/login');
  };

  const handleDeleteAccount = async () => {
    setLoading(true);
    try {
      const customerId = localStorage.getItem('customerId');
      const customerEmail = localStorage.getItem('customerEmail');

      if (!customerId || !customerEmail) {
        setNotification({
          type: 'error',
          message: 'Customer information not found'
        });
        return;
      }

      // Delete customer orders
      const { error: ordersError } = await supabase
        .from('orders')
        .delete()
        .eq('customer_email', customerEmail);

      if (ordersError) {
        console.error('Error deleting orders:', ordersError);
      }

      // Delete customer reviews
      const { error: reviewsError } = await supabase
        .from('reviews')
        .delete()
        .eq('customer_email', customerEmail);

      if (reviewsError) {
        console.error('Error deleting reviews:', reviewsError);
      }

      // Delete customer profile
      const { error: customerError } = await supabase
        .from('customers')
        .delete()
        .eq('id', customerId);

      if (customerError) {
        console.error('Error deleting customer profile:', customerError);
        setNotification({
          type: 'error',
          message: 'Failed to delete account. Please try again.'
        });
        return;
      }

      // Clear local storage
      localStorage.removeItem('customerId');
      localStorage.removeItem('customerEmail');
      localStorage.removeItem('customerName');
      
      // Logout and redirect
      logout();
      setNotification({
        type: 'success',
        message: 'Account deleted successfully'
      });
      
      // Redirect to home page after a short delay
      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (error) {
      console.error('Error deleting account:', error);
      setNotification({
        type: 'error',
        message: 'An error occurred while deleting your account. Please try again.'
      });
    } finally {
      setLoading(false);
      setShowDeleteAccountModal(false);
    }
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  const closeOrderModal = () => {
    setSelectedOrder(null);
    setShowOrderModal(false);
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 4000);
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const customerId = localStorage.getItem('customerId');
      
      const { error } = await supabase
        .from('customers')
        .update({
          first_name: profileForm.first_name,
          last_name: profileForm.last_name,
          phone: profileForm.phone,
          updated_at: new Date().toISOString()
        })
        .eq('id', customerId);

      if (error) {
        throw error;
      }

      // Update localStorage
      localStorage.setItem('customerName', `${profileForm.first_name} ${profileForm.last_name}`);
      setCustomerInfo(prev => ({
        ...prev,
        name: `${profileForm.first_name} ${profileForm.last_name}`
      }));

      showNotification('Profile updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating profile:', error);
      showNotification('Error updating profile. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getOrderStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return '#28a745';
      case 'shipped': return '#007bff';
      case 'delivered': return '#28a745';
      case 'cancelled': return '#dc3545';
      default: return '#6c757d';
    }
  };



  const renderNotification = () => {
    if (!notification) return null;

    const isSuccess = notification.type === 'success';
    const isError = notification.type === 'error';

    return (
      <div className={`notification ${notification.type}`}>
        <div className="notification-content">
          <div className="notification-icon">
            {isSuccess ? '✅' : isError ? '❌' : 'ℹ️'}
          </div>
          <div className="notification-message">
            {notification.message}
          </div>
          <button 
            className="notification-close"
            onClick={() => setNotification(null)}
          >
            ×
          </button>
        </div>
        <div className="notification-progress"></div>
      </div>
    );
  };

  const renderOrders = () => (
    <div className="dashboard-orders">
      <h2>My Orders</h2>
      {loading ? (
        <div className="loading">Loading orders...</div>
      ) : orders.length === 0 ? (
        <div className="no-orders">
          <p>You haven't placed any orders yet.</p>
          <button className="shop-btn" onClick={() => navigate('/products')}>
            Start Shopping
          </button>
        </div>
      ) : (
        <div className="orders-table">
          <table>
            <thead>
              <tr>
                <th>Order #</th>
                <th>Date</th>
                <th>Total</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(order => (
                <tr key={order.id}>
                  <td>{order.order_number}</td>
                  <td>{new Date(order.created_at).toLocaleDateString()}</td>
                  <td>£{parseFloat(order.total || 0).toFixed(2)}</td>
                  <td>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getOrderStatusColor(order.status) }}
                    >
                      {order.status}
                    </span>
                  </td>
                  <td>
                    <button 
                      className="view-btn"
                      onClick={() => handleViewOrder(order)}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderReviews = () => (
    <div className="dashboard-reviews">
      <h2>Product Reviews</h2>
      
      {/* Review Opportunities */}
      {reviewOpportunities.length > 0 && (
        <div className="review-opportunities">
          <h3>Write Reviews</h3>
          <p>You can now review these products from your delivered orders:</p>
          <div className="opportunities-grid">
            {reviewOpportunities.map(opportunity => (
              <div key={opportunity.id} className="opportunity-card">
                <div className="product-info">
                  {opportunity.products?.image_url && (
                    <img 
                      src={opportunity.products.image_url} 
                      alt={opportunity.products.name}
                      className="product-image"
                    />
                  )}
                  <div className="product-details">
                    <h4>{opportunity.products?.name}</h4>
                    <span className="verified-badge">✓ Verified Purchase</span>
                  </div>
                </div>
                <button 
                  className="write-review-btn"
                  onClick={() => handleWriteReview(opportunity)}
                >
                  Write Review
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Existing Reviews */}
      <div className="existing-reviews">
        <h3>My Reviews</h3>
        {reviews.length === 0 ? (
          <div className="no-reviews">
            <p>You haven't written any reviews yet.</p>
            {reviewOpportunities.length === 0 && (
              <p>Reviews will appear here after you receive and can review delivered orders.</p>
            )}
          </div>
        ) : (
          <div className="reviews-list">
            {reviews.map(review => (
              <div key={review.id} className="review-card">
                <div className="review-header">
                  <div className="product-info">
                    {review.products?.image_url && (
                      <img 
                        src={review.products.image_url} 
                        alt={review.products.name}
                        className="product-image-small"
                      />
                    )}
                    <div>
                      <h4>{review.products?.name}</h4>
                      <div className="rating">
                        {'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}
                        <span className="rating-number">({review.rating}/5)</span>
                      </div>
                    </div>
                  </div>
                  <div className="review-status">
                    <span className={`status-badge ${review.status}`}>
                      {review.status === 'pending' ? 'Under Review' : review.status}
                    </span>
                    {review.verified_purchase && (
                      <span className="verified-badge">✓ Verified Purchase</span>
                    )}
                  </div>
                </div>
                <div className="review-content">
                  {review.title && <h5 className="review-title">{review.title}</h5>}
                  {review.comment && <p className="review-comment">{review.comment}</p>}
                  <span className="review-date">
                    {new Date(review.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderProfile = () => (
    <div className="dashboard-profile">
      <h2>My Profile</h2>
      <form onSubmit={handleProfileUpdate} className="profile-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="first_name">First Name</label>
            <input
              type="text"
              id="first_name"
              value={profileForm.first_name}
              onChange={(e) => setProfileForm({...profileForm, first_name: e.target.value})}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="last_name">Last Name</label>
            <input
              type="text"
              id="last_name"
              value={profileForm.last_name}
              onChange={(e) => setProfileForm({...profileForm, last_name: e.target.value})}
              required
            />
          </div>
        </div>
        
        <div className="form-group">
          <label htmlFor="email">Email Address</label>
          <input
            type="email"
            id="email"
            value={profileForm.email}
            disabled
            className="disabled-input"
          />
          <small>Email cannot be changed</small>
        </div>
        
        <div className="form-group">
          <label htmlFor="phone">Phone Number</label>
          <input
            type="tel"
            id="phone"
            value={profileForm.phone}
            onChange={(e) => setProfileForm({...profileForm, phone: e.target.value})}
          />
        </div>
        
        <button type="submit" className="update-btn" disabled={loading}>
          {loading ? 'Updating...' : 'Update Profile'}
        </button>
        
        <div className="danger-zone">
          <h3>Danger Zone</h3>
          <p>Once you delete your account, there is no going back. Please be certain.</p>
          <button 
            type="button" 
            className="delete-account-btn"
            onClick={() => setShowDeleteAccountModal(true)}
            disabled={loading}
          >
            Delete Account
          </button>
        </div>
      </form>
    </div>
  );

  if (!customerInfo) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="customer-dashboard-page">
      <div className="customer-dashboard-container">
        <div className="dashboard-sidebar">
          <div className="customer-info">
            <h3>My Account</h3>
            <p>Welcome, {customerInfo.name}!</p>
          </div>
          
          <nav className="dashboard-nav">
            <button 
              className={`nav-item ${activeSection === 'orders' ? 'active' : ''}`}
              onClick={() => setActiveSection('orders')}
            >
              <span>🛒</span> My Orders
            </button>
            <button 
              className={`nav-item ${activeSection === 'reviews' ? 'active' : ''}`}
              onClick={() => setActiveSection('reviews')}
            >
              <span>⭐</span> Reviews
              {reviewOpportunities.length > 0 && (
                <span className="notification-badge">{reviewOpportunities.length}</span>
              )}
            </button>
            <button 
              className={`nav-item ${activeSection === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveSection('profile')}
            >
              <span>👤</span> Profile
            </button>
          </nav>
          
          <button className="logout-btn" onClick={handleLogout}>
            <span>🚪</span> Logout
          </button>
        </div>

        <div className="dashboard-main">
          {activeSection === 'orders' && renderOrders()}
          {activeSection === 'reviews' && renderReviews()}
          {activeSection === 'profile' && renderProfile()}
        </div>
      </div>

      {/* Order Detail Modal */}
      {showOrderModal && selectedOrder && (
        <div className="modal-overlay" onClick={closeOrderModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Order Details</h3>
              <button className="close-btn" onClick={closeOrderModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="order-details">
                <div className="detail-row">
                  <span className="detail-label">Order Number:</span>
                  <span className="detail-value">{selectedOrder.order_number}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Order Date:</span>
                  <span className="detail-value">
                    {new Date(selectedOrder.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Status:</span>
                  <span className="detail-value">
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getOrderStatusColor(selectedOrder.status) }}
                    >
                      {selectedOrder.status}
                    </span>
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Payment Method:</span>
                  <span className="detail-value">{selectedOrder.payment_method}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Subtotal:</span>
                  <span className="detail-value">£{parseFloat(selectedOrder.subtotal || 0).toFixed(2)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Tax:</span>
                  <span className="detail-value">{getCurrencySymbol()}{parseFloat(selectedOrder.tax || 0).toFixed(2)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Shipping:</span>
                  <span className="detail-value">{getCurrencySymbol()}{parseFloat(selectedOrder.shipping || 0).toFixed(2)}</span>
                </div>
                <div className="detail-row total">
                  <span className="detail-label">Total:</span>
                  <span className="detail-value">{getCurrencySymbol()}{parseFloat(selectedOrder.total || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && selectedReviewOpportunity && (
        <div className="modal-overlay" onClick={() => setShowReviewModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Write Review</h3>
              <button className="close-btn" onClick={() => setShowReviewModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="review-product-info">
                {selectedReviewOpportunity.products?.image_url && (
                  <img 
                    src={selectedReviewOpportunity.products.image_url} 
                    alt={selectedReviewOpportunity.products.name}
                    className="review-product-image"
                  />
                )}
                <div>
                  <h4>{selectedReviewOpportunity.products?.name}</h4>
                  <span className="verified-badge">✓ Verified Purchase</span>
                </div>
              </div>
              
              <form className="review-form">
                <div className="form-group">
                  <label>Rating</label>
                  <div className="star-rating">
                    {[1, 2, 3, 4, 5].map(star => (
                      <button
                        key={star}
                        type="button"
                        className={`star ${star <= reviewForm.rating ? 'active' : ''}`}
                        onClick={() => setReviewForm(prev => ({ ...prev, rating: star }))}
                      >
                        ★
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Review Title (Optional)</label>
                  <input
                    type="text"
                    value={reviewForm.title}
                    onChange={(e) => setReviewForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Summarize your review"
                    maxLength="100"
                  />
                </div>
                
                <div className="form-group">
                  <label>Review (Optional)</label>
                  <textarea
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm(prev => ({ ...prev, comment: e.target.value }))}
                    placeholder="Tell others about your experience with this product"
                    rows="4"
                    maxLength="1000"
                  />
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button 
                className="submit-review-btn"
                onClick={handleSubmitReview}
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Submit Review'}
              </button>
              <button 
                className="cancel-btn"
                onClick={() => setShowReviewModal(false)}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteAccountModal && (
        <div className="modal-overlay" onClick={() => {
          setShowDeleteAccountModal(false);
        }}>
          <div className="modal-content delete-account-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Account</h3>
              <button className="close-btn" onClick={() => {
                setShowDeleteAccountModal(false);
              }}>×</button>
            </div>
            <div className="modal-body">
              <div className="warning-message">
                <div className="warning-icon">⚠️</div>
                <h4>Are you sure you want to delete your account?</h4>
                <p>This action cannot be undone. Your account and all data will be permanently removed.</p>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="delete-account-confirm-btn"
                onClick={handleDeleteAccount}
                disabled={loading}
              >
                {loading ? 'Deleting...' : 'Delete Now'}
              </button>
              <button 
                className="cancel-btn"
                onClick={() => {
                  setShowDeleteAccountModal(false);
                }}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {notification && renderNotification()}
    </div>
  );
};

export default CustomerDashboard; 